import { Avatar, Badge, Box } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getAbsenceTypeID } from 'src/modules/calendar/components/helper';
import { AttendenceDTO } from 'src/services/api/models/attendanceDto';
import { RootStateType } from 'src/services/redux/reducers';
import { Colors } from 'src/shared/components/styles/colors';
import SantaHat from 'src/assets/images/santahat-02.png';
import { useGlobals } from 'src/services/contexts/appContext';
import PartyHat from 'src/assets/images/party_hat05.png';
import { useIsBirthday } from 'src/services/helpers/useIsBirthday';

type AvatarWithBorderMeProps = {
  size: number;
  userToShow: AttendenceDTO;
  showThemeHat: boolean;
  smallHat: boolean;
};

export const AvatarWithBorderMe = (props: AvatarWithBorderMeProps) => {
  const { size, userToShow, showThemeHat, smallHat } = props;
  const { loggedInUser } = useGlobals();
  const { user } = useSelector((state: RootStateType) => state.oidc);
  const { t } = useTranslation();

  // För att kunna visa partyhatt vid födelsedag och tomteluva vid december
  const today = new Date();
  const isDecember = today.getMonth() === 11;
  const isBirthday = useIsBirthday(loggedInUser?.Personnumber);

  const getBorderColor = useCallback(() => {
    const result = {
      color: Colors.GREEN,
      text: t('news:checkedIn'),
    };
    switch (true) {
      case userToShow.onDuty:
        result.color = Colors.GREEN; // INCHECKAD TRUMFAR ÖVER ALLA ANDRA OAVSETT CURRENTABSENCE
        result.text = t('news:checkedIn');
        break;

      case !userToShow.onDuty && userToShow.currentAbsence === null:
        result.color = Colors.GRAY_BORDER; // UTCHECKAD UTAN FRÅNVAROTYP
        result.text = t('news:checkedOut');
        break;

      case !userToShow.onDuty && getAbsenceTypeID(userToShow.currentAbsence) === 1:
        result.color = Colors.RED; // LEDIGHET
        result.text = t('news:absence');
        break;

      case !userToShow.onDuty && getAbsenceTypeID(userToShow.currentAbsence) === 2:
        result.color = Colors.YELLOW; // TJÄNSTEÄRENDE
        result.text = t('news:businessTripAbsence');
        break;

      case !userToShow.onDuty && getAbsenceTypeID(userToShow.currentAbsence) === 3:
        result.color = Colors.GREEN; // DISTANS MEN INTE CHECKAT IN
        result.text = t('news:remoteAbsence');
        break;

      default:
        result.color = Colors.GRAY_BORDER;
        result.text = t('news:checkedOut');
        break;
    }

    return result;
  }, [userToShow.currentAbsence, userToShow.onDuty, t]);

  const { color, text } = getBorderColor();

  return (
    <Box>
      <Box>
        {smallHat && showThemeHat && (
          <>
            {isDecember && !isBirthday && (
              <img
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: size - 10,
                  zIndex: 1,
                  transform: 'rotate(-23deg)',
                  marginTop: 3,
                  marginLeft: 3,
                }}
                src={SantaHat}
                alt='santahat'
              />
            )}
            {isBirthday && (
              <img
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 3,
                  width: size - 20,
                  height: size - 18,
                  zIndex: 1,
                  transform: 'rotate(-40deg)',
                }}
                src={PartyHat}
                alt='partyhat'
              />
            )}
          </>
        )}
      </Box>
      {!smallHat && showThemeHat && (
        <Box sx={{ position: 'relative', height: '30px' }}>
          {isDecember && !isBirthday && (
            <img
              style={{
                position: 'absolute',
                top: 15,
                left: -13,
                right: 35,
                width: 40 * 1.16,
                height: 38 * 1.16,
                zIndex: 1,
                transform: 'rotate(-23deg)',
              }}
              src={SantaHat}
              alt='santahat'
            />
          )}
          {isBirthday && (
            <img
              style={{
                top: 12,
                left: -14,
                right: 35,
                zIndex: 1,
                position: 'absolute',
                width: 26 * 1.16,
                height: 34 * 1.16,
                transform: 'rotate(-45deg)',
              }}
              src={PartyHat}
              alt='partyhat'
            />
          )}
        </Box>
      )}
      <Badge
        sx={{
          '& .MuiBadge-badge': {
            backgroundColor: color,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            padding: '0 5px',
            borderRadius: '12px',
            marginRight: '35%',
          },
        }}
        overlap='circular'
        badgeContent={text}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Avatar
          src={`${userToShow.userImage}?access_token=${user?.access_token}`}
          sx={{
            width: size,
            height: size,
            borderRadius: '50%',
            border: `${size < 21 ? '1px' : '3px'}`,
          }}
        />
      </Badge>
    </Box>
  );
};
